import logo from "@/assets/cloudspry_white_logo.png"
import { Cloud, Loader2 } from "lucide-react"
import React from "react"

interface LoadingSpinnerProps {
  size?: "sm" | "md" | "lg"
  className?: string
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({ size = "md", className = "" }) => {
  const sizeClasses = {
    sm: "h-4 w-4",
    md: "h-6 w-6",
    lg: "h-8 w-8",
  }

  return <Loader2 className={`animate-spin ${sizeClasses[size]} ${className}`} />
}

export const LoadingPage: React.FC = () => {
  return (
    <div className="relative flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-950 overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Floating cloud animations */}
        <div className="absolute top-20 left-10 opacity-20 dark:opacity-10 animate-float-slow">
          <Cloud className="w-24 h-24 text-primary-300 dark:text-primary-700" />
        </div>
        <div className="absolute top-40 right-20 opacity-20 dark:opacity-10 animate-float-medium">
          <Cloud className="w-32 h-32 text-primary-200 dark:text-primary-800" />
        </div>
        <div className="absolute bottom-32 left-1/4 opacity-20 dark:opacity-10 animate-float-fast">
          <Cloud className="w-20 h-20 text-primary-400 dark:text-primary-600" />
        </div>

        {/* Gradient orbs */}
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-primary-200/30 dark:bg-primary-900/20 rounded-full blur-3xl animate-pulse-slow" />
        <div className="absolute bottom-1/4 left-1/4 w-80 h-80 bg-teal-200/30 dark:bg-teal-900/20 rounded-full blur-3xl animate-pulse-slower" />
      </div>

      {/* Main content */}
      <div className="relative z-10 text-center px-4">
        {/* Logo with fade-in animation */}
        <div className="mb-8 animate-fade-in-up">
          <div className="relative inline-block">
            {/* Glow effect behind logo */}
            <div className="absolute inset-0 blur-2xl bg-primary-400/30 dark:bg-primary-500/20 rounded-full scale-150" />

            {/* Logo */}
            <div className="relative bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-gray-200/50 dark:border-gray-700/50">
              <img
                src={logo}
                alt="CloudSpry"
                className="h-16 w-auto mx-auto filter dark:brightness-110 animate-subtle-bounce"
              />
            </div>
          </div>
        </div>

        {/* Loading spinner */}
        <div className="mb-6 animate-fade-in-up animation-delay-200">
          <div className="relative inline-block">
            {/* Spinning ring */}
            <div className="w-16 h-16 rounded-full border-4 border-gray-200 dark:border-gray-700 border-t-primary-500 animate-spin mx-auto" />

            {/* Inner glow */}
            <div className="absolute inset-0 rounded-full bg-primary-400/20 dark:bg-primary-500/10 blur-md animate-pulse" />
          </div>
        </div>

        {/* Loading text */}
        <div className="animate-fade-in-up animation-delay-400">
          <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-2">Loading CloudSpry</h2>
          <p className="text-gray-600 dark:text-gray-400 text-sm">Preparing your cloud cost management dashboard...</p>
        </div>
      </div>

      {/* Custom animations */}
      <style>{`
        @keyframes float-slow {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          50% { transform: translateY(-20px) translateX(10px); }
        }

        @keyframes float-medium {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          50% { transform: translateY(-15px) translateX(-15px); }
        }

        @keyframes float-fast {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          50% { transform: translateY(-25px) translateX(15px); }
        }

        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes subtle-bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }

        @keyframes bounce-dot {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }

        @keyframes pulse-slow {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.1); }
        }

        @keyframes pulse-slower {
          0%, 100% { opacity: 0.2; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(1.15); }
        }

        .animate-float-slow {
          animation: float-slow 6s ease-in-out infinite;
        }

        .animate-float-medium {
          animation: float-medium 5s ease-in-out infinite;
        }

        .animate-float-fast {
          animation: float-fast 4s ease-in-out infinite;
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.6s ease-out forwards;
        }

        .animate-subtle-bounce {
          animation: subtle-bounce 2s ease-in-out infinite;
        }

        .animate-bounce-dot {
          animation: bounce-dot 1.4s ease-in-out infinite;
        }

        .animate-pulse-slow {
          animation: pulse-slow 4s ease-in-out infinite;
        }

        .animate-pulse-slower {
          animation: pulse-slower 5s ease-in-out infinite;
        }

        .animation-delay-0 {
          animation-delay: 0s;
        }

        .animation-delay-150 {
          animation-delay: 0.15s;
        }

        .animation-delay-200 {
          animation-delay: 0.2s;
        }

        .animation-delay-300 {
          animation-delay: 0.3s;
        }

        .animation-delay-400 {
          animation-delay: 0.4s;
        }

        .animation-delay-600 {
          animation-delay: 0.6s;
        }
      `}</style>
    </div>
  )
}

export const LoadingCard: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  return (
    <div className="card p-6">
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <LoadingSpinner size="md" className="mx-auto mb-2 text-gray-600 dark:text-gray-400" />
          <p className="text-sm text-gray-500 dark:text-gray-400">{children || "Loading..."}</p>
        </div>
      </div>
    </div>
  )
}
